import { motion } from 'motion/react';
import { memo, useMemo } from 'react';
import { MGSearchResultCard } from '@/features/GlobalSearch/components/search/MGSearchResultCard';
import { SearchButton } from '@/features/GlobalSearch/components/search/SearchButton';
import { PopularSearchCarousel } from '@/features/GlobalSearch/components/shared/PopularSearchCarousel';
import { SearchCarousel } from '@/features/GlobalSearch/components/shared/SearchCarousel';
import { SectionHeading } from '@/features/GlobalSearch/components/trending/SectionHeading';
import { getTrendingResultUrl } from '@/features/GlobalSearch/utils/getTrendingSearches';
import { MG_TRENDING_CAROUSEL_DATA } from '@/fixtures/motionGraphicsCarouselData';
import useTWScreens from '@/hooks/ui/useTWScreens';
import type { AssetType } from '@/features/GlobalSearch/types/index';
import type { GenericSearchResult } from '@/features/SearchPage/types';
import type { MotionGraphics, SearchResponseHit } from '@music-vine/setlist';
interface MotionTrendingSectionProps {
  closePopup: () => void;
  onNameClick: (content: string | undefined, endSlug: string | undefined, assetType: AssetType | 'general') => void;
  trackTrendingClick: (endSlug: string) => void;
  trendingMotionAssets?: {
    hits?: SearchResponseHit<GenericSearchResult>[];
  };
  trendingMotionSearches?: {
    hits?: SearchResponseHit<GenericSearchResult>[];
  };
}
export const MotionTrendingSection = memo(({
  trendingMotionSearches,
  trendingMotionAssets,
  closePopup,
  trackTrendingClick,
  onNameClick
}: MotionTrendingSectionProps) => {
  const {
    isMd
  } = useTWScreens();

  // Format the MG collections data to show all collections in carousel format
  const collectionsData = useMemo(() => {
    // Format all collections data for the carousel
    return MG_TRENDING_CAROUSEL_DATA.map(item => ({
      document: {
        id: `mg-collection-${item.id}`,
        name: item.label,
        slug: item.link,
        image: item.image,
        asset_type: []
      },
      highlight: {},
      text_match: 100
    }));
  }, []);

  // Prepare asset data
  const assetsData = trendingMotionAssets?.hits ?? [];

  // Determine which section is the last visible one for proper margin
  const hasCollections = collectionsData.length > 0;
  const hasAssets = assetsData.length > 0;

  // Use motion.div only on desktop, regular div on mobile to avoid iOS Safari touch issues
  const AnimatedContainer = isMd ? motion.div : 'div';
  const AnimatedItem = isMd ? motion.div : 'div';
  return <AnimatedContainer animate={isMd ? {
    opacity: 1
  } : undefined} initial={isMd ? {
    opacity: 0
  } : undefined} style={{
    position: 'relative'
  }} transition={isMd ? {
    duration: 0.15
  } : undefined}>
        <div className="main-search-results my-4 px-4 md:my-0 md:mb-5 md:px-0">
          <div className="mb-2">
            <SectionHeading>Popular searches</SectionHeading>
          </div>

          {/* Desktop view */}
          <AnimatedItem className="hidden max-h-6 overflow-hidden md:flex md:flex-wrap md:gap-2 focus-within:[&:has(:focus-visible)]:max-h-none focus-within:[&:has(:focus-visible)]:overflow-visible">
            {trendingMotionSearches?.hits?.slice(0, 6).map((hit, idx) => {
          const {
            document
          } = hit;
          const href = getTrendingResultUrl({
            name: document.name ?? '',
            type: 'motion'
          });
          return <AnimatedItem animate={isMd ? {
            opacity: 1
          } : undefined} initial={isMd ? {
            opacity: 0
          } : undefined} key={document.id} transition={isMd ? {
            duration: 0.15,
            delay: Math.min(idx * 0.02, 0.1)
          } : undefined}>
                  <SearchButton assetType="motion" closePopup={closePopup} href={href} id={document.id} name={(document.name ?? '').toLowerCase()} onNameClick={onNameClick} testId="popular-mg-search" />
                </AnimatedItem>;
        })}
          </AnimatedItem>

          {/* Mobile Carousel */}
          <div className="-mx-4 block md:hidden">
            <PopularSearchCarousel assetType="motion" closePopup={closePopup} hits={trendingMotionSearches?.hits} onNameClick={onNameClick} testId="popular-mg-search" />
          </div>
        </div>

        {hasCollections && <div className="main-search-results my-4 px-4 md:my-0 md:mb-5 md:px-0">
            <div className="mb-2">
              <SectionHeading>Collections</SectionHeading>
            </div>
            {/* Collections Carousel for both desktop and mobile */}
            <SearchCarousel assetType="motion" closePopup={closePopup} data={collectionsData} onTileClick={trackTrendingClick} />
          </div>}

        {hasAssets && <div className="main-search-results my-4 px-4 md:my-0 md:mb-0 md:px-0">
            <div className="mb-2">
              <SectionHeading>Motion assets</SectionHeading>
            </div>
            <AnimatedItem className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {assetsData.slice(0, 4).map((hit, idx) => {
          const motionGraphic = hit.document as unknown as MotionGraphics;
          return <AnimatedItem animate={isMd ? {
            opacity: 1
          } : undefined} initial={isMd ? {
            opacity: 0
          } : undefined} key={`mg-asset-${motionGraphic.id}`} transition={isMd ? {
            duration: 0.2,
            delay: Math.min(idx * 0.05, 0.25)
          } : undefined}>
                    <MGSearchResultCard compatibility={motionGraphic.compatibility} contributor={motionGraphic.contributor.name} isLut={motionGraphic.is_lut} isNew={false} name={motionGraphic.name} onClick={(slug: string) => {
              closePopup();
              trackTrendingClick(`/motion-graphics/asset/${slug}`);
            }} posterUrl={motionGraphic.image} previewVideoUrl={motionGraphic.preview_video_url} slug={motionGraphic.slug} variant="globalSearch" />
                  </AnimatedItem>;
        })}
            </AnimatedItem>
          </div>}
      </AnimatedContainer>;
});
MotionTrendingSection.displayName = 'MotionTrendingSection';